import {
  TrackGroup,
  TrackItem,
  TrackThumbnail,
  TrackType,
} from '@monorepo/remotion-ninja';
import { upperFirst } from 'lodash';

export const getTrackItemPosition = (
  item: TrackItem
): { left: string; width: string } => {
  const left =
    typeof item.left === 'number' ? `${item.left}%` : item.left || '0';
  const width =
    typeof item.width === 'number' ? `${item.width}%` : item.width || 'auto';

  return { left, width };
};

export const getTrackItemVerticalPosition = (
  item: TrackItem
): { top: string; height: string } => {
  const top = typeof item.top === 'number' ? `${item.top}%` : item.top || '0';
  const height =
    typeof item.height === 'number' ? `${item.height}%` : item.height || 'auto';

  return { top, height };
};

export const getTrackType = (trackGroup: TrackGroup): TrackType => {
  if (trackGroup.type === 'asset') {
    // For asset track groups, determine type based on the first item
    const firstItem = trackGroup.tracks[0];
    if (firstItem) {
      return firstItem.type;
    }
    return TrackType.Image; // Default if no items
  }
  return trackGroup.type;
};

export const getTrackColor = (trackType: TrackType | string): string => {
  switch (trackType) {
    case TrackType.Video:
      return '#1677ff'; // Blue
    case TrackType.Image:
      return '#000'; // Green
    case TrackType.Text:
      return '#fa8c16'; // Orange
    case TrackType.Audio:
      return '#00ab84'; // Purple
    case TrackType.Voiceover:
      return '#c41d7f'; // Pink
    case TrackType.Gif:
      return '#13c2c2'; // Cyan
    case TrackType.Thumbnail:
      return '#faad14'; // Yellow
    case 'track':
      return '#1677ff'; // Blue for default track
    default:
      return '#8c8c8c'; // Gray (default)
  }
};

export const getTrackBorderColor = (trackType: TrackType | string): string => {
  switch (trackType) {
    case TrackType.Video:
      return '#0958d9'; // Darker blue to complement #1677ff
    case TrackType.Image:
      return '#434343'; // Dark gray to complement black
    case TrackType.Text:
      return '#d46b08'; // Darker orange to complement #fa8c16
    case TrackType.Audio:
      return '#00ab84'; // Darker pink to complement #eb2f96
    case TrackType.Voiceover:
      return '#c41d7f'; // Slightly darker pink to complement #eb2f96
    case TrackType.Gif:
      return '#08979c'; // Darker cyan to complement #13c2c2
    case TrackType.Thumbnail:
      return '#d48806'; // Darker yellow to complement #faad14
    case 'track':
      return '#0958d9'; // Darker blue to complement #1677ff
    default:
      return '#595959'; // Darker gray to complement #8c8c8c
  }
};

export const getTrackLabel = (trackType: TrackType | string): string => {
  return 'Track'; // All tracks are now just labeled as "Track"
};

export const getTrackHeight = (trackType: TrackType | string): number => {
  return 40; // Same fixed height for all track types
};

// Calculate track item timeline position based on frames
export const getTrackItemTimelinePosition = (
  item: TrackItem,
  totalFrames: number
): { startPercent: number; widthPercent: number } => {
  const startPercent = (item.from / totalFrames) * 100;
  const widthPercent = (item.durationInFrames / totalFrames) * 100;

  return { startPercent, widthPercent };
};

// Function to get item label based on data type
export const getTrackItemLabel = (item: TrackItem): string => {
  const data = item.data;

  if (!data) return '';

  if (item.type === TrackType.Thumbnail) {
    return `${upperFirst((data as TrackThumbnail).type)} Thumbnail`;
  }

  // Handle text/thumbnail items
  if ('text' in data && typeof data.text === 'string') {
    return data.text;
  }

  // Handle asset items
  if ('src' in data && typeof data.src === 'string') {
    const src = data.src;
    const parts = src.split('/');
    return parts[parts.length - 1];
  }

  // Handle audio items
  if ('startAt' in data) {
    return 'Audio';
  }

  return `Item ${item.from}`;
};
